// @core/stores/payment/PaymentStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { paymentApi } from '@core/api';
import type {
  AuthorizePaymentRequest,
  AuthorizePaymentResponse,
  CapturePaymentRequest,
  CapturePaymentResponse,
  ChargePaymentRequest,
  ChargePaymentResponse,
  Payment,
  RefundPaymentRequest,
  RefundPaymentResponse,
  VoidPaymentRequest,
  VoidPaymentResponse,
} from '@core/types/payment';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class PaymentStore {
  // List state
  items: Payment[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Payment | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Payment>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Payment | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Payment | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Authorize payment
   */
  async AuthorizePayment(data: AuthorizePaymentRequest): Promise<AuthorizePaymentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.AuthorizePayment(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Capture authorized payment
   */
  async CapturePayment(id: string, data: CapturePaymentRequest): Promise<CapturePaymentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.CapturePayment(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Charge payment
   */
  async ChargePayment(data: ChargePaymentRequest): Promise<ChargePaymentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.ChargePayment(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get payment by ID
   */
  async GetPayment(id: string): Promise<Payment> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await paymentApi.GetPayment(id);
      const item = response.payment;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List payments
   */
  async ListPayments(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await paymentApi.ListPayments(params);

      runInAction(() => {
        this.items = response.payments?.payments ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Refund payment
   */
  async RefundPayment(id: string, data: RefundPaymentRequest): Promise<RefundPaymentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.RefundPayment(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Void authorized payment
   */
  async VoidPayment(id: string, data: VoidPaymentRequest): Promise<VoidPaymentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.VoidPayment(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const paymentStore = new PaymentStore();
